import { useHydrated } from "../hydrated";
import { Title } from "./Hl";
import { useEffect, useState, type ReactNode } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { t } from "../i18n";
import { CopyBlock } from "./CopyBlock";
import { GuideHost } from "./Guide";
import { CLAUDE_PLUGINS_URL } from "./ClaudeLinks";
import { CHATGPT_PLUGIN_URL } from "./ChatGpt";
import { GuideFigure } from "./GuideFigure";
import { FeedbackForm } from "./FeedbackForm";
import { useSession } from "../session";
import { CaretDownIcon } from "./icons";

export const CONNECTOR_URL = "https://marketing-kit.app/mcp";
export const START_MESSAGE = "check v-marketing-kit status";
/** The plugin's own first suggestion in ChatGPT; there it is named V-Kit. */
export const CHATGPT_START_MESSAGE = "@V-Kit Check my V-Kit connections";
export const PLUGIN_MARKETPLACE = "arey325/marketing-kit-plugin";
/** The plugin's name in Claude - what to search for on the Plugins page (ADR-0038). */
export const PLUGIN_NAME = "v-marketing-kit";
/** Claude Code: the plugin and its marketplace are both named v-marketing-kit (ADR-0038). */
export const PLUGIN_INSTALL_COMMAND = "claude plugin install v-marketing-kit@v-marketing-kit";
export const MARKETPLACE_ADD_COMMAND = `claude plugin marketplace add ${PLUGIN_MARKETPLACE}`;
/** This browser's last pick on the Claude | ChatGPT | Other apps (MCP) switch (ADR-0032). */
export const ASSISTANT_KEY = "mk-install-assistant";

type Assistant = "claude" | "chatgpt" | "mcp";
const ASSISTANTS: Assistant[] = ["claude", "chatgpt", "mcp"];
const PICK_LABEL = { claude: "install.pickClaude", chatgpt: "install.pickChatgpt", mcp: "install.pickMcp" } as const;
const SHORT_NAME = { claude: "install.pickClaude", chatgpt: "install.pickChatgpt", mcp: "install.mcpShort" } as const;

/** Anchors pick the assistant before the remembered choice does. */
export function assistantFromHash(hash: string): Assistant | null {
  const id = decodeURIComponent(hash.replace(/^#/, ""));
  if (id === "chatgpt" || id === "chatgpt-steps" || id === "chatgpt-more" || id === "gpt-token") return "chatgpt";
  if (id === "plugin" || id === "claude" || id === "start") return "claude";
  // Old anchors of «Other ways» and the downloads open «Other apps (MCP)» (owner 2026-11-02).
  if (["mcp", "other-apps", "other-ways", "connector", "claude-code", "advanced", "downloads"].includes(id)) return "mcp";
  return null;
}

function storedAssistant(): Assistant | null {
  if (typeof window === "undefined") return null; // pre-render (ADR-0036): the default card
  try {
    const v = localStorage.getItem(ASSISTANT_KEY);
    return ASSISTANTS.includes(v as Assistant) ? (v as Assistant) : null;
  } catch {
    return null;
  }
}

export function InstallPage() {
  const { me } = useSession();
  const navigate = useNavigate();
  const { hash } = useLocation();
  // The build renders the Claude card; the anchor and the remembered choice apply after hydration (ADR-0050).
  const hydrated = useHydrated();
  const [picked, setAssistant] = useState<Assistant | null>(null);
  const assistant: Assistant = picked ?? (hydrated ? (assistantFromHash(hash) ?? storedAssistant() ?? "claude") : "claude");

  useEffect(() => {
    // The migration left /install (owner 2026-11-02); the server instructions still link /install#migrate.
    if (decodeURIComponent(hash.replace(/^#/, "")) === "migrate") {
      navigate("/guide#migrate", { replace: true });
      return;
    }
    const a = assistantFromHash(hash);
    if (a) setAssistant(a);
  }, [hash, navigate]);

  function pick(a: Assistant) {
    setAssistant(a);
    try {
      localStorage.setItem(ASSISTANT_KEY, a);
    } catch {
      /* storage blocked: the choice lasts for this page only */
    }
  }

  const others = ASSISTANTS.filter((a) => a !== assistant);

  return (
    <>
      {/* The page itself is the plugin and ChatGPT instruction: only source anchors open a modal here. */}
      <GuideHost signedIn={!!me} skip={["plugin", "chatgpt"]} />
      <div className="install-top">
        <h1><Title text={t("install.title")} dot="tangerine" /></h1>
        <p className="lead">{t("install.lead")}</p>
        <div className="install-pick">
          <div className="seg" role="radiogroup" aria-label={t("install.pickLabel")} data-testid="assistant-pick">
            {ASSISTANTS.map((a) => (
              <label key={a} className="seg-opt">
                <input type="radio" name="assistant" value={a} checked={assistant === a} onChange={() => pick(a)} />
                {t(PICK_LABEL[a])}
              </label>
            ))}
          </div>
          <span className="install-pick-note">{t("install.pickBoth")}</span>
        </div>
      </div>

      {assistant === "claude" ? (
        <section className="card elev-md install-card install-main" id="plugin" data-testid="claude-card">
          <span id="start" className="anchor" aria-hidden="true" />
          <div className="install-body">
            <div className="install-head">
              <span className="card-kicker">{t("install.pluginKicker")}</span>
              <span className="tag tag-accent">{t("install.pluginBadge")}</span>
            </div>
            <h2>{t("install.pluginTitle")}</h2>
            <p className="install-lead">{t("install.pluginLead")}</p>
          </div>
          <ol className="mk-steps roomy" data-testid="claude-steps">
            <Step n={1}>
              <span>
                {t("install.plugin1a")}
                <a href={CLAUDE_PLUGINS_URL} target="_blank" rel="noreferrer">
                  {t("install.plugin1Link")}
                </a>
              </span>
              <span className="sub">{t("install.plugin1Sub")}</span>
            </Step>
            <Step n={2}>
              <span>
                {t("install.plugin2a")}
                <em>{t("install.plugin2Em")}</em>
                {t("install.plugin2b")}
              </span>
              <CopyBlock inline text={PLUGIN_MARKETPLACE} />
              <GuideFigure
                testId="fig-add-marketplace"
                summary={t("install.figWhere")}
                src="/guides/claude-plugins-add-marketplace.png"
                alt={t("install.fig2Alt")}
                boxes={[
                  { left: 77.8, top: 4.3, width: 20.6, height: 13.5 },
                  { left: 55, top: 23, width: 42.2, height: 13 },
                ]}
                pins={[
                  { n: 1, left: 77.8, top: 17.8, shift: [-55, -50] },
                  { n: 2, left: 55, top: 23 },
                ]}
                caption={[
                  <>
                    <em>{t("install.fig2Cap1Em")}</em>
                    {t("install.fig2Cap1")}
                  </>,
                  <>
                    <em>{t("install.fig2Cap2Em")}</em>
                    {t("install.fig2Cap2")}
                  </>,
                ]}
              />
            </Step>
            <Step n={3}>
              <span>
                {t("install.plugin3a")}
                <em>{t("install.plugin3Em")}</em>
                {t("install.plugin3b")}
                <em>{t("install.plugin3Em2")}</em>
              </span>
              <CopyBlock inline text={PLUGIN_NAME} />
              <span className="sub">{t("install.plugin3Sub")}</span>
              <GuideFigure
                testId="fig-search"
                summary={t("install.figLooks")}
                src="/guides/claude-plugins-search.png"
                alt={t("install.fig3Alt")}
                boxes={[{ left: 11.1, top: 47, width: 57.8, height: 42 }]}
                pins={[{ n: 1, left: 11.1, top: 47 }]}
                caption={[
                  <>
                    {t("install.fig3Cap1a")}
                    <em>{t("install.fig3Cap1Em")}</em>
                    {t("install.fig3Cap1b")}
                  </>,
                ]}
              />
            </Step>
            <Step n={4}>
              <span>
                {t("install.plugin4a")}
                <em>{t("install.plugin4Em")}</em>
                {t("install.plugin4Arrow")}
                <em>{t("install.plugin4Em2")}</em>
                {t("install.plugin4b")}
              </span>
              <span className="sub">{t("install.plugin4Sub")}</span>
              <GuideFigure
                testId="fig-connect"
                summary={t("install.figWhere")}
                src="/guides/claude-plugin-connect.png"
                alt={t("install.fig4Alt")}
                boxes={[
                  { left: 52.7, top: 1, width: 23.5, height: 21 },
                  { left: 83.9, top: 72.4, width: 15.5, height: 21 },
                ]}
                pins={[
                  { n: 1, left: 52.7, top: 22, shift: [-55, -50] },
                  { n: 2, left: 83.9, top: 72.4 },
                ]}
                caption={[
                  <>
                    {t("install.fig4Cap1a")}
                    <em>{t("install.fig4Cap1Em")}</em>
                    {t("install.fig4Cap1b")}
                  </>,
                  <>
                    <em>{t("install.fig4Cap2Em")}</em>
                    {t("install.fig4Cap2")}
                  </>,
                ]}
              />
            </Step>
          </ol>
          <div className="install-then">
            <span className="install-then-title">{t("install.thenTitle")}</span>
            <span className="install-then-text">
              {t("install.then1")}
              <Link to="/connections">{t("install.thenLink")}</Link>
              {t("install.then2")}
            </span>
            <CopyBlock inline text={START_MESSAGE} />
          </div>
          <p className="install-clients" data-testid="install-clients">
            {t("install.clients")}
            <Link to="/connections">{t("install.clientsLink")}</Link>.
          </p>

          <p className="install-note" data-testid="install-phone">
            {t("install.phoneNote")}
            <Link to="/guide#claude-code">{t("install.phoneNoteLink")}</Link>.
          </p>
        </section>
      ) : assistant === "chatgpt" ? (
        /* ChatGPT - the V-Kit plugin from the ChatGPT plugin directory, any plan (ADR-0066). */
        <section className="card elev-md install-card install-main" id="chatgpt" data-testid="chatgpt-card">
          <div className="install-body">
            <div className="install-head">
              <span className="card-kicker">{t("install.chatgptKicker")}</span>
              <span className="tag tag-accent">{t("install.chatgptBadge")}</span>
            </div>
            <h2>{t("install.chatgptTitle")}</h2>
            <p className="install-lead">{t("install.chatgptLead")}</p>
          </div>
          <ol className="mk-steps roomy" id="chatgpt-steps" data-testid="chatgpt-steps">
            <Step n={1}>
              <span>
                {t("install.chatgpt1a")}
                <a href={CHATGPT_PLUGIN_URL} target="_blank" rel="noreferrer" data-testid="chatgpt-plugin-link">
                  {t("install.chatgpt1Link")}
                </a>
                {t("install.chatgpt1b")}
                <em>{t("install.chatgpt1Em")}</em>
              </span>
              <span className="sub">{t("install.chatgpt1Sub")}</span>
              <GuideFigure
                testId="fig-chatgpt-install"
                summary={t("install.figWhere")}
                src="/guides/chatgpt-plugin-install.png"
                alt={t("install.figChatgpt1Alt")}
                boxes={[{ left: 80.3, top: 9.2, width: 16.6, height: 9.7 }]}
                pins={[{ n: 1, left: 80.3, top: 9.2 }]}
                caption={[
                  <>
                    <em>{t("install.chatgpt1Em")}</em>
                    {t("install.figChatgpt1Cap")}
                  </>,
                ]}
              />
            </Step>
            <Step n={2}>
              <span>
                {t("install.chatgpt2a")}
                <em>{t("install.chatgpt2Em")}</em>
                {t("install.chatgpt2b")}
                <em>{t("install.chatgpt2Em2")}</em>
              </span>
              <GuideFigure
                testId="fig-chatgpt-connect"
                summary={t("install.figWhere")}
                src="/guides/chatgpt-plugin-connect.png"
                alt={t("install.figChatgpt2Alt")}
                boxes={[{ left: 5.2, top: 79, width: 89.6, height: 7.2 }]}
                pins={[{ n: 1, left: 5.2, top: 79 }]}
                caption={[
                  <>
                    <em>{t("install.chatgpt2Em")}</em>
                    {t("install.figChatgpt2Cap")}
                  </>,
                ]}
              />
            </Step>
            <Step n={3}>
              <span>
                {t("install.chatgpt3a")}
                <em>{t("install.chatgpt3Em")}</em>
                {t("install.chatgpt3b")}
              </span>
              <CopyBlock inline text={CHATGPT_START_MESSAGE} />
              <span className="sub">
                {t("install.chatgpt3Sub")}
                <Link to="/connections">{t("install.thenLink")}</Link>.
              </span>
            </Step>
          </ol>
          <p className="install-note">{t("install.chatgptNote")}</p>
          <p className="install-clients" data-testid="install-clients">
            {t("install.clients")}
            <Link to="/connections">{t("install.clientsLink")}</Link>.
          </p>
        </section>
      ) : (
        <McpCard />
      )}

      <div className="install-also" data-testid="install-also">
        <span>{t("install.alsoAny")}</span>
        {others.map((a) => (
          <button
            key={a}
            type="button"
            className="btn btn-ghost"
            onClick={() => {
              pick(a);
              window.scrollTo?.({ top: 0, behavior: "smooth" });
            }}
          >
            {t("install.showSteps", { name: t(SHORT_NAME[a]) })}
          </button>
        ))}
      </div>

      <FeedbackForm signedIn={!!me} />
    </>
  );
}

/** «Other apps (MCP)» (owner 2026-11-02): any MCP app by URL with the same OAuth sign-in, and how that sign-in works. */
function McpCard() {
  return (
    <section className="card elev-md install-card install-main" id="mcp" data-testid="mcp-card">
      <span id="other-apps" className="anchor" aria-hidden="true" />
      <div className="install-body">
        <div className="install-head">
          <span className="card-kicker">{t("install.mcpKicker")}</span>
          <span className="tag tag-accent">{t("install.mcpBadge")}</span>
        </div>
        <h2>{t("install.mcpTitle")}</h2>
        <p className="install-lead">{t("install.mcpLead")}</p>
      </div>
      <ol className="mk-steps roomy" data-testid="mcp-steps">
        <Step n={1}>
          <span>{t("install.mcp1")}</span>
          <CopyBlock inline text={CONNECTOR_URL} />
          <span className="sub">{t("install.mcp1Sub")}</span>
        </Step>
        <Step n={2}>
          <span>
            {t("install.mcp2a")}
            <em>{t("install.mcp2Em")}</em>
            {t("install.mcp2b")}
          </span>
          <span className="sub">{t("install.plugin4Sub")}</span>
        </Step>
        <Step n={3}>
          <span>
            {t("install.then1")}
            <Link to="/connections">{t("install.thenLink")}</Link>
            {t("install.mcp3")}
          </span>
          <CopyBlock inline text={START_MESSAGE} />
        </Step>
      </ol>
      {/* How the OAuth sign-in goes, step by step (owner 2026-11-02: «рассказать как авторизуются OAuth»). Facts:
          server/src/mcp/routes.ts (401 + resource_metadata), oauth/routes.ts (discovery, DCR, CIMD, S256 only),
          oauth/store.ts (access 1 h; refresh 90 days, renewed on each refresh), consent names the client (ADR-0066), revoke - /connections#assistants. */}
      <div className="fold-body other-ways mcp-auth" data-testid="mcp-auth">
        <section>
          <h3>{t("install.authTitle")}</h3>
          <ol className="mcp-auth-steps">
            <li>{t("install.auth1")}</li>
            <li>{t("install.auth2")}</li>
            <li>{t("install.auth3")}</li>
            <li>{t("install.auth4")}</li>
            <li>
              {t("install.auth5")}
              <Link to="/connections#assistants">{t("install.auth5Link")}</Link>.
            </li>
          </ol>
          <p>{t("install.authNoOauth")}</p>
        </section>
      </div>
      <p className="install-clients" data-testid="install-clients">
        {t("install.clients")}
        <Link to="/connections">{t("install.clientsLink")}</Link>.
      </p>

    </section>
  );
}

/** «Had Marketing Kit before?» (ADR-0038): removing the old marketing-kit items. Also on /guide#migrate. */
export function MigrateSteps({ heading = 3 }: { heading?: 3 | 4 | 5 }) {
  const H = `h${heading}` as "h3";
  return (
    <>
      <p>{t("install.migrateLead")}</p>
      <section>
        <H>{t("install.migratePluginTitle")}</H>
        <p>{t("install.migratePlugin")}</p>
      </section>
      <section>
        <H>{t("install.migrateCodeTitle")}</H>
        <p>{t("install.migrateCode")}</p>
        <CopyBlock inline text={t("install.migrateCmdUninstall")} />
        <CopyBlock inline text={t("install.migrateCmdRemove")} />
        <CopyBlock inline text={MARKETPLACE_ADD_COMMAND} />
        <CopyBlock inline text={PLUGIN_INSTALL_COMMAND} />
      </section>
      <section>
        <H>{t("install.migrateExtTitle")}</H>
        <p>{t("install.migrateExt")}</p>
      </section>
      <section>
        <H>{t("install.migrateSkillTitle")}</H>
        <p>{t("install.migrateSkill")}</p>
      </section>
      <section>
        <H>{t("install.migrateConnectorTitle")}</H>
        <p>{t("install.migrateConnector")}</p>
      </section>
    </>
  );
}

/** One step in the mockup's form: a numbered circle, a short line, muted sub-lines, an inline copy box. */
function Step({ n, children }: { n: number; children: ReactNode }) {
  return (
    <li>
      <span className="mk-num" aria-hidden="true">
        {n}
      </span>
      <div className="mk-step">{children}</div>
    </li>
  );
}
